// Preferências de UI puramente locais (não sync com servidor). Coisas que
// não fazem sentido espelhar entre dispositivos — escolhas visuais do aluno
// no equipamento atual, tipo de qual lado a sidebar de questões fica.
// Persiste em localStorage.

import { signal } from "@preact/signals";

const STORAGE_KEY = "newstudent:ui-prefs";

export type QuestionListSide = "left" | "right";
export type QuestionListIndicator = "stripe" | "dot" | "none";
export type AiProvider = "gemini" | "openai" | "anthropic" | "xai" | "deepseek";
export type AiAccessMode = "platform" | "byok" | "connected";
export type AiModelCatalogId = string;

export interface QuestionListBadgesPrefs {
  caso: boolean;
  imagem: boolean;
  tabela: boolean;
  dificuldade: boolean;
  anulada: boolean;
  /** Tempo gasto na questão (mm:ss). Default on. */
  tempo: boolean;
}

interface UiPrefs {
  questionListSide: QuestionListSide;
  questionListCollapsed: boolean;
  provaHeaderCompact: boolean;
  questionListPreviewLines: 0 | 1 | 2 | 3;
  questionListIndicator: QuestionListIndicator;
  /** Pinta o row INTEIRO da lista por status (verde/vermelho/âmbar/violeta).
   *  Reforça a leitura rápida, mas pode poluir em provas longas — toggle. */
  questionListRowTint: boolean;
  questionListBadges: QuestionListBadgesPrefs;
  /** O que o aluno escolheu deixar visível no header em telas pequenas
   *  (md-) quando o espaço é escasso. Default: summary on, timers off. */
  headerMobileShowSummary: boolean;
  headerMobileShowTimers: boolean;
  /** Assistente IA — sidebar à direita no Modo Estudo. */
  aiAssistantEnabled: boolean;
  aiAssistantOpen: boolean;
  /** Largura da sidebar de IA em px. Redimensionável pelo aluno via
   *  drag handle. Clampada entre AI_WIDTH_MIN e AI_WIDTH_MAX. */
  aiAssistantWidth: number;
  /** Modo de acesso da IA: plataforma (chave compartilhada) ou BYOK. */
  aiAccessMode: AiAccessMode;
  /** Provedor ativo para a conversa atual. */
  aiProvider: AiProvider;
  /** Modelo opcional escolhido pelo aluno. Vazio = default do provedor. */
  aiModel: string;
  /** Id da opcao curada escolhida pelo aluno. */
  aiModelCatalogId: AiModelCatalogId;
  /** BYOK opcional. Quando aiAccessMode=byok e apiKey preenchido, o
   *  backend usa a chave do aluno em vez da chave servidor. */
  aiByokApiKey: string;
  /** ID do usuário que salvou a apiKey acima. Quando o usuário ativo
   *  muda (logout/login com outra conta no mesmo navegador), a key
   *  é limpa automaticamente — outro aluno NÃO pode usar/copiar a key
   *  do anterior. */
  aiByokSavedForUserId: string;
}

type LegacyUiPrefs = Partial<UiPrefs> & {
  aiByokProvider?: "none" | "gemini";
};

// Mínimo: caber chat com input + ícones (320px é o w-80 do Tailwind).
// Máximo absoluto: 800px — em telas ultrawide ainda dá pra ler bem o
// chat sem virar uma coluna gigante. O limite REAL durante o drag é
// dinâmico (computeAiMaxWidth) — leva em conta a viewport, a sidebar
// do app, a lista de questões e largura mínima de leitura da questão.
export const AI_WIDTH_MIN = 320;
export const AI_WIDTH_MAX = 800;
export const AI_WIDTH_DEFAULT = 384;

/** Calcula o teto real do AI baseado no espaço livre da viewport.
 *  Considera: sidebar do app (w-72 ou colapsada), padding da main,
 *  lista de questões (se aberta), gaps e min de leitura da questão
 *  (≈ 560px pra texto + alternativas). */
export function computeAiMaxWidth(opts: {
  appSidebarCollapsed: boolean;
  questionListCollapsed: boolean;
}): number {
  if (typeof window === "undefined") return AI_WIDTH_MAX;
  const w = window.innerWidth;
  const appSidebar = opts.appSidebarCollapsed ? 64 : 288;
  const mainPadding = 64; // px-8 dos dois lados
  const list = opts.questionListCollapsed ? 0 : 320; // lg:w-80
  const gaps = opts.questionListCollapsed ? 20 : 40; // gap-5 entre blocos
  const questionMin = 560; // min pra questão ficar legível
  const reserved = appSidebar + mainPadding + list + gaps + questionMin;
  const room = w - reserved;
  return Math.max(AI_WIDTH_MIN, Math.min(AI_WIDTH_MAX, room));
}

const DEFAULT_BADGES: QuestionListBadgesPrefs = {
  caso: true,
  imagem: true,
  tabela: true,
  dificuldade: true,
  anulada: true,
  tempo: true
};

const DEFAULT: UiPrefs = {
  questionListSide: "right",
  questionListCollapsed: false,
  provaHeaderCompact: false,
  questionListPreviewLines: 2,
  questionListIndicator: "stripe",
  questionListRowTint: false,
  questionListBadges: DEFAULT_BADGES,
  headerMobileShowSummary: true,
  headerMobileShowTimers: false,
  aiAssistantEnabled: true,
  aiAssistantOpen: false,
  aiAssistantWidth: AI_WIDTH_DEFAULT,
  aiAccessMode: "platform",
  aiProvider: "gemini",
  aiModel: "",
  aiModelCatalogId: "",
  aiByokApiKey: "",
  aiByokSavedForUserId: ""
};

function load(): UiPrefs {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw) as LegacyUiPrefs;
    const previewLines =
      parsed.questionListPreviewLines === 0 ||
      parsed.questionListPreviewLines === 1 ||
      parsed.questionListPreviewLines === 2 ||
      parsed.questionListPreviewLines === 3
        ? parsed.questionListPreviewLines
        : DEFAULT.questionListPreviewLines;
    const indicator =
      parsed.questionListIndicator === "stripe" ||
      parsed.questionListIndicator === "dot" ||
      parsed.questionListIndicator === "none"
        ? parsed.questionListIndicator
        : DEFAULT.questionListIndicator;
    return {
      ...DEFAULT,
      ...parsed,
      questionListSide:
        parsed.questionListSide === "left" || parsed.questionListSide === "right"
          ? parsed.questionListSide
          : DEFAULT.questionListSide,
      aiAccessMode:
        parsed.aiAccessMode === "byok" ||
        parsed.aiAccessMode === "platform" ||
        parsed.aiAccessMode === "connected"
          ? parsed.aiAccessMode
          : parsed.aiByokProvider && parsed.aiByokProvider !== "none"
            ? "byok"
            : DEFAULT.aiAccessMode,
      aiProvider:
        parsed.aiProvider === "gemini" ||
        parsed.aiProvider === "openai" ||
        parsed.aiProvider === "anthropic" ||
        parsed.aiProvider === "xai" ||
        parsed.aiProvider === "deepseek"
          ? parsed.aiProvider
          : parsed.aiByokProvider === "gemini"
            ? "gemini"
            : DEFAULT.aiProvider,
      aiModel: typeof parsed.aiModel === "string" ? parsed.aiModel : DEFAULT.aiModel,
      aiModelCatalogId:
        typeof (parsed as { aiModelCatalogId?: unknown }).aiModelCatalogId === "string"
          ? ((parsed as { aiModelCatalogId?: string }).aiModelCatalogId ?? "")
          : DEFAULT.aiModelCatalogId,
      questionListPreviewLines: previewLines,
      questionListIndicator: indicator,
      questionListBadges: { ...DEFAULT_BADGES, ...(parsed.questionListBadges ?? {}) },
      aiByokApiKey:
        typeof parsed.aiByokApiKey === "string" ? parsed.aiByokApiKey : "",
      aiByokSavedForUserId:
        typeof parsed.aiByokSavedForUserId === "string"
          ? parsed.aiByokSavedForUserId
          : ""
    };
  } catch {
    return DEFAULT;
  }
}

function persist(next: UiPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignora
  }
}

export const uiPrefs = signal<UiPrefs>(load());

export function toggleQuestionListSide() {
  const next: UiPrefs = {
    ...uiPrefs.value,
    questionListSide:
      uiPrefs.value.questionListSide === "left" ? "right" : "left"
  };
  uiPrefs.value = next;
  persist(next);
}

export function setQuestionListSide(side: QuestionListSide) {
  const next: UiPrefs = { ...uiPrefs.value, questionListSide: side };
  uiPrefs.value = next;
  persist(next);
}

export function toggleQuestionListCollapsed() {
  const next: UiPrefs = {
    ...uiPrefs.value,
    questionListCollapsed: !uiPrefs.value.questionListCollapsed
  };
  uiPrefs.value = next;
  persist(next);
}

export function toggleProvaHeaderCompact() {
  const next: UiPrefs = {
    ...uiPrefs.value,
    provaHeaderCompact: !uiPrefs.value.provaHeaderCompact
  };
  uiPrefs.value = next;
  persist(next);
}

export function setQuestionListPreviewLines(n: 0 | 1 | 2 | 3) {
  const next: UiPrefs = { ...uiPrefs.value, questionListPreviewLines: n };
  uiPrefs.value = next;
  persist(next);
}

export function setQuestionListIndicator(v: QuestionListIndicator) {
  const next: UiPrefs = { ...uiPrefs.value, questionListIndicator: v };
  uiPrefs.value = next;
  persist(next);
}

export function toggleQuestionListRowTint() {
  const next: UiPrefs = {
    ...uiPrefs.value,
    questionListRowTint: !uiPrefs.value.questionListRowTint
  };
  uiPrefs.value = next;
  persist(next);
}

export function toggleQuestionListBadge(key: keyof QuestionListBadgesPrefs) {
  const next: UiPrefs = {
    ...uiPrefs.value,
    questionListBadges: {
      ...uiPrefs.value.questionListBadges,
      [key]: !uiPrefs.value.questionListBadges[key]
    }
  };
  uiPrefs.value = next;
  persist(next);
}

export function toggleHeaderMobileShowSummary() {
  const next: UiPrefs = {
    ...uiPrefs.value,
    headerMobileShowSummary: !uiPrefs.value.headerMobileShowSummary
  };
  uiPrefs.value = next;
  persist(next);
}

export function toggleHeaderMobileShowTimers() {
  const next: UiPrefs = {
    ...uiPrefs.value,
    headerMobileShowTimers: !uiPrefs.value.headerMobileShowTimers
  };
  uiPrefs.value = next;
  persist(next);
}

export function toggleAiAssistantOpen() {
  const next: UiPrefs = {
    ...uiPrefs.value,
    aiAssistantOpen: !uiPrefs.value.aiAssistantOpen
  };
  uiPrefs.value = next;
  persist(next);
}

export function setAiAssistantEnabled(v: boolean) {
  const next: UiPrefs = {
    ...uiPrefs.value,
    aiAssistantEnabled: v,
    // Se desabilitar, também fecha pra não deixar o painel pairando
    aiAssistantOpen: v ? uiPrefs.value.aiAssistantOpen : false
  };
  uiPrefs.value = next;
  persist(next);
}

export function setAiAssistantWidth(px: number) {
  const clamped = Math.max(AI_WIDTH_MIN, Math.min(AI_WIDTH_MAX, Math.round(px)));
  const next: UiPrefs = { ...uiPrefs.value, aiAssistantWidth: clamped };
  uiPrefs.value = next;
  persist(next);
}

export function setAiAccessMode(mode: AiAccessMode) {
  const normalizedMode =
    mode === "connected" && uiPrefs.value.aiProvider !== "gemini"
      ? "platform"
      : mode;
  const next: UiPrefs = {
    ...uiPrefs.value,
    aiAccessMode: normalizedMode
  };
  uiPrefs.value = next;
  persist(next);
}

export function setAiProvider(provider: AiProvider) {
  const next: UiPrefs = {
    ...uiPrefs.value,
    aiProvider: provider,
    aiAccessMode:
      uiPrefs.value.aiAccessMode === "connected" && provider !== "gemini"
        ? "platform"
        : uiPrefs.value.aiAccessMode,
    aiModelCatalogId: ""
  };
  uiPrefs.value = next;
  persist(next);
}

export function setAiModel(model: string) {
  const next: UiPrefs = {
    ...uiPrefs.value,
    aiModel: model,
    aiModelCatalogId: ""
  };
  uiPrefs.value = next;
  persist(next);
}

export function setAiModelPreset(modelCatalogId: AiModelCatalogId, model: string) {
  const next: UiPrefs = {
    ...uiPrefs.value,
    aiModel: model,
    aiModelCatalogId: modelCatalogId
  };
  uiPrefs.value = next;
  persist(next);
}

export function setAiByok(
  provider: AiProvider,
  apiKey: string,
  userId: string
) {
  const next: UiPrefs = {
    ...uiPrefs.value,
    aiAccessMode: "byok",
    aiProvider: provider,
    aiByokApiKey: apiKey,
    aiByokSavedForUserId: userId
  };
  uiPrefs.value = next;
  persist(next);
}

// Limpa qualquer credencial de IA salva localmente. Chamado em:
//   - logout (proteção: chave do user A não fica acessível pro user B
//     que faça login no mesmo navegador)
//   - bootstrap quando aiByokSavedForUserId !== currentUserId
//   - usuário escolhe explicitamente "Esquecer chave"
export function clearAiCredentials() {
  const next: UiPrefs = {
    ...uiPrefs.value,
    aiAccessMode: "platform",
    aiByokApiKey: "",
    aiByokSavedForUserId: ""
  };
  uiPrefs.value = next;
  persist(next);
}
